import type { SpeechModel } from 'ai';
import {
  BaseSpeechProvider,
  type SpeechProviderAdapter,
  TaskStatus,
  type CreateTaskConfig,
  type CreateTaskResult,
  type PollTaskConfig,
  type PollTaskResult,
} from '../base';
import { getValueByPath, isStatusMatch } from '../tools';
import {
  kieSunoStatusMapping,
} from './config';
import {
  buildCreatePayloadFromConfig,
  getKieSunoModelConfig,
} from './utils';
import type { KieSunoCreateResponse, KieSunoTaskResponse } from './types';

const provider = new BaseSpeechProvider(
  {
    baseUrlEnv: 'KIE_AI_BASE_URL',
    apiKeyEnv: 'KIE_AI_API_KEY',
    debugEnv: 'KIE_AI_DEBUG',
    providerName: 'KIE',
  },
  {
    timeoutMs: 10 * 60 * 1000,
    backoff: {
      initialDelayMs: 3000,
      maxSteps: 20,
      scale: 1,
      jitter: 0.2,
      maxDelayMs: 8000,
    },
  }
);

const adapter: SpeechProviderAdapter = {
  buildCreateTaskRequest(modelId, params, context): CreateTaskConfig {
    const cfg = getKieSunoModelConfig(modelId);
    const payload = buildCreatePayloadFromConfig(modelId, params);
    return {
      url: `${context.baseUrl}${cfg.createPath}`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${context.apiKey}`,
      },
      body: payload,
    };
  },

  parseCreateTaskResponse(modelId, response, data: KieSunoCreateResponse): CreateTaskResult {
    const taskId = getValueByPath(data, kieSunoStatusMapping.taskIdPath);
    if (!taskId) {
      throw new Error('Failed to get task id from response');
    }
    return { taskId: String(taskId), raw: data };
  },

  buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
    const cfg = getKieSunoModelConfig(modelId);
    return {
      url: (id) => `${context.baseUrl}${cfg.pollPath}?taskId=${id}`,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${context.apiKey}`,
      },
    };
  },

  parsePollTaskResponse(modelId, response, data: KieSunoTaskResponse): PollTaskResult {
    const state = getValueByPath(data, kieSunoStatusMapping.statePath);
    if (state === undefined) {
      return { status: TaskStatus.PROCESSING, raw: data };
    }

    if (isStatusMatch(state, kieSunoStatusMapping.success)) {
      const audioUrlValue = getValueByPath(data, kieSunoStatusMapping.audioUrlPath);
      const audioUrls: string[] = Array.isArray(audioUrlValue)
        ? audioUrlValue.filter(Boolean).map(String)
        : (audioUrlValue ? [String(audioUrlValue)] : []);
      if (!audioUrls.length) {
        return {
          status: TaskStatus.FAILED,
          error: 'Missing audio url',
          raw: data,
        };
      }
      const lyrics = kieSunoStatusMapping.lyricsPath
        ? getValueByPath(data, kieSunoStatusMapping.lyricsPath)
        : undefined;
      return {
        status: TaskStatus.SUCCESS,
        audioUrls,
        providerMetadata: {
          lyrics: typeof lyrics === 'string' && lyrics.trim() ? lyrics : undefined,
        },
        raw: data,
      };
    }

    if (isStatusMatch(state, kieSunoStatusMapping.failed)) {
      const err = getValueByPath(data, kieSunoStatusMapping.errorPath);
      return {
        status: TaskStatus.FAILED,
        error: String(err) || 'unknown error',
        raw: data,
      };
    }

    if (isStatusMatch(state, kieSunoStatusMapping.processing)) {
      return { status: TaskStatus.PROCESSING, raw: data };
    }

    return { status: TaskStatus.PROCESSING, raw: data };
  },
};

export const kie_suno = {
  speech: (modelId: string): SpeechModel => {
    const cfg = getKieSunoModelConfig(modelId);
    return provider.createSpeechModel(modelId, adapter, cfg.timeoutMs);
  },
};